import { useState, useRef } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HoneypotField, readHoneypot, submitErrorMessage } from "@/components/HoneypotField";
import axios from "axios";
import { API } from "@/App";
import { Upload, FileText, CheckCircle, AlertCircle, X } from "lucide-react";

export const DocumentsPage = () => {
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    document_type: "",
    purpose: "",
    message: ""
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  
  const documentTypes = [
    { value: "identite", label: "Pièce d'identité (CNI, Passeport)" },
    { value: "revenus", label: "Justificatifs de revenus (3 derniers bulletins)" },
    { value: "avis_imposition", label: "Avis d'imposition" },
    { value: "contrat_travail", label: "Contrat de travail" },
    { value: "quittances_loyer", label: "3 dernières quittances de loyer" },
    { value: "promesse_embauche", label: "Promesse d'embauche" },
    { value: "taxe_fonciere", label: "Taxe foncière" },
    { value: "attestation_hebergement", label: "Attestation d'hébergement" },
    { value: "attestation_apl", label: "Attestation APL" },
    { value: "attestation_caf", label: "Attestation allocations familiales" },
    { value: "autre", label: "Autre document" }
  ];
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    e.target.value = "";
    if (selectedFile) {
      const isPdf = selectedFile.type === "application/pdf" || selectedFile.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        toast.error("Seuls les fichiers PDF sont acceptés");
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("Le fichier ne doit pas dépasser 10 Mo");
        return;
      }
      setFile(selectedFile);
    }
  };
  
  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const website = readHoneypot(e);
    
    if (!file) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }
    
    setSubmitting(true);
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Data = reader.result.split(',')[1];
      const documentData = {
        ...formData,
        website,
        file_name: file.name,
        file_data: base64Data,
        file_type: file.type
      };
      try {
        await axios.post(`${API}/documents`, documentData);
        toast.success("Votre dossier a été soumis avec succès !");
        setSubmitted(true);
      } catch (error) {
        toast.error(submitErrorMessage(error));
        setSubmitting(false);
      }
    };
    reader.readAsDataURL(file);
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="documents-page-success">
        <Header />
        
        <section className="section-padding">
          <div className="container-custom max-w-2xl text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl mb-4">Dossier envoyé !</h1>
            <p className="text-gray-600 mb-8">
              Votre document a été transmis à notre équipe. Nous l'examinerons dans les plus brefs délais
              et vous contacterons si des informations complémentaires sont nécessaires.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    client_name: "",
                    client_email: "",
                    client_phone: "",
                    document_type: "",
                    purpose: "",
                    message: ""
                  });
                  setFile(null);
                }}
                className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
                data-testid="submit-another-document"
              >
                Envoyer un autre document
              </Button>
              <Button variant="outline" onClick={() => window.location.href = "/"}>
                Retour à l'accueil
              </Button>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white" data-testid="documents-page">
      <Header />
      
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Dossier client
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
            Déposez votre dossier
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Pour faciliter le traitement de votre demande d'achat ou de location,
            déposez vos documents en toute sécurité via notre plateforme.
          </p>
        </div>
      </section>
      
      {/* Info Banner */}
      <section className="bg-[#F9F9F9] py-6 border-b border-gray-100">
        <div className="container-custom">
          <div className="flex items-start gap-4 text-sm">
            <AlertCircle className="text-[#D4AF37] flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-[#0A0A0A]">Documents sécurisés</p>
              <p className="text-gray-600">
                Vos documents sont transmis de manière sécurisée et ne seront utilisés 
                que dans le cadre de votre dossier immobilier. Format accepté : PDF uniquement (max. 10 Mo).
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Form */}
      <section className="section-padding">
        <div className="container-custom max-w-3xl">
          <form onSubmit={handleSubmit} className="space-y-8 relative" data-testid="document-upload-form">
            <HoneypotField />
            {/* Personal Info */}
            <div className="bg-[#F9F9F9] p-6 md:p-8 rounded-sm">
              <h2 className="font-serif text-xl mb-6">Vos informations</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Nom complet *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Votre nom et prénom"
                    required
                    data-testid="document-client-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="client_email">Email *</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="votre@email.com"
                    required
                    data-testid="document-client-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="client_phone">Téléphone *</Label>
                  <Input
                    id="client_phone"
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                    placeholder="06 00 00 00 00"
                    required
                    data-testid="document-client-phone"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="purpose">Référence du bien *</Label>
                  <Input
                    id="purpose"
                    value={formData.purpose}
                    onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                    placeholder="Ex : REF-2024-012 (indiquée sur l'annonce)"
                    required
                    data-testid="document-purpose"
                  />
                </div>
              </div>
            </div>
            
            {/* Document Upload */}
            <div className="bg-[#F9F9F9] p-6 md:p-8 rounded-sm">
              <h2 className="font-serif text-xl mb-6">Document à déposer</h2>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="document_type">Type de document *</Label>
                  <Select
                    value={formData.document_type || undefined}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, document_type: value }))}
                  >
                    <SelectTrigger data-testid="document-type">
                      <SelectValue placeholder="Sélectionnez le type de document" />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* File Upload Zone */}
                <div className="space-y-2">
                  <Label>Fichier *</Label>
                  
                  {!file ? (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-sm p-8 text-center hover:border-[#D4AF37] transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="file-upload-zone"
                    >
                      <Upload className="mx-auto mb-4 text-gray-400" size={40} />
                      <p className="text-gray-600 mb-2">
                        Cliquez ou glissez votre fichier ici
                      </p>
                      <p className="text-sm text-gray-400">
                        PDF uniquement • Max. 10 Mo
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="file-input"
                      />
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-sm p-4 flex items-center justify-between bg-white" data-testid="file-preview">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D4AF37]/10 rounded flex items-center justify-center">
                          <FileText className="text-[#D4AF37]" size={20} />
                        </div>
                        <div>
                          <p className="font-medium text-sm truncate max-w-xs">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} Mo
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 hover:bg-gray-100 rounded transition-colors"
                        data-testid="remove-file"
                      >
                        <X size={18} className="text-gray-500" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Message (optionnel)</Label>
                  <Textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Informations complémentaires sur votre dossier..."
                    data-testid="document-message"
                  />
                </div>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] py-6"
              disabled={submitting || !file}
              data-testid="document-submit-button"
            >
              {submitting ? (
                "Envoi en cours..."
              ) : (
                <>
                  <Upload size={18} className="mr-2" />
                  Envoyer mon document
                </>
              )}
            </Button>
          </form>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default DocumentsPage;
